import { getRoles, getUserRoles, getUserRolesByToken } from "../services/auth";
import { IRole, IRoles, IUserRole, IUserRoles } from "../models/IUserRoles";

export const SUPER_ADMIN_ROLE_ID = "8a4f28f7-2b6c-ef11-986b-5cef2cd6e8d8";
export const LIVE_CHAT_AGENT_ROLE_ID = "8556f0b1-2f0f-4b40-98d2-7186babbeaca";
export const RESTAURANT_DRIVER_ROLE_ID = "3f8b5c7d-4a2e-4f1d-b6c9-8e3a7d5f2b1c";
// "Kullanıcı Yönetimi" > Restoran sekmesinde restoran kullanıcıları oluşturulurken kullanılan rol id'si.
export const RESTAURANT_ADMIN_ROLE_ID = "0cbe4ee5-2b6c-ef11-adfb-5bef2cd6e8d8";

export const checkIsSuperAdmin = async (userId: string): Promise<boolean> => {
  try {
    const roleListResponse = await getRoles();
    const roles: IRoles = roleListResponse.data;
    const userRoleResponse= await getUserRoles(userId);
    const userRoles:IUserRoles=userRoleResponse.data;

     // return roles.some((role) => role.id === "SuperAdmin" && role.id === userRole.id);
     return userRoles.some(role => role.role.id === SUPER_ADMIN_ROLE_ID);

  } catch (err) {
    console.error("Rol kontrolü hatası:", err);
    return false;
  }
};

export interface ILiveChatAccess {
  isSuperAdmin: boolean;
  isLiveChatAgent: boolean;
}

/**
 * LiveChatAgentLogin ile giriş yapan kullanıcının rollerini, o an localStorage'daki
 * token üzerinden (userId parametresi gerekmeden) kontrol eder.
 * SuperAdmin ise tam erişim, sadece LiveChatAgent rolü varsa kısıtlı erişim verilir.
 */
export const checkLiveChatAccess = async (): Promise<ILiveChatAccess> => {
  const userRoleResponse = await getUserRolesByToken();
  const userRoles: IUserRoles = userRoleResponse.data;
  return {
    isSuperAdmin: userRoles.some((r) => r.roleId === SUPER_ADMIN_ROLE_ID),
    isLiveChatAgent: userRoles.some((r) => r.roleId === LIVE_CHAT_AGENT_ROLE_ID),
  };
};

/**
 * Garson hesabı açma yetkisi. `RegisterWaiterUser` restoranı **çağıranın token'ından**
 * çözüyor (tam olarak bir erişilebilir restoran olmalı), bu yüzden SuperAdmin bu ucu
 * kullanamaz — ekranda da yalnız RestourantAdmin'e gösterilmeli.
 */
export const checkIsRestaurantAdmin = async (): Promise<boolean> => {
  const userRoleResponse = await getUserRolesByToken();
  const userRoles: IUserRoles = userRoleResponse.data;
  return userRoles.some((r) => r.roleId === RESTAURANT_ADMIN_ROLE_ID);
};

/**
 * Salon Siparişi ekranını SuperAdmin dışındaki kullanıcılara açan rol (Roller sekmesinden
 * eklenmiş, backend kodunda geçmeyen bir rol). Yalnız menü/ekran görünürlüğünü belirler;
 * salon uçlarının yetkisi backend'de RestourantAdmin/SuperAdmin/Garson ile verilir.
 */
export const SALON_ORDER_ROLE_ID = "1629d1b3-f9d2-447e-b269-bcb2fbbbc4b7";

// Navbar her sayfada yeniden kurulduğu için sonuç kısa süre önbellekte tutulur. Süre kısa
// tutulur ki oturum açıkken verilen/geri alınan rol, sayfa yenilemeden kısa sürede yansısın.
const SALON_ROLE_CACHE_MS = 60_000;
let salonRoleCache: { token: string; at: number; result: Promise<boolean> } | null = null;

/** Rol ataması gibi işlemlerden sonra bir sonraki kontrolün sunucuya gitmesini sağlar. */
export const invalidateSalonRoleCache = () => { salonRoleCache = null; };

export const checkHasSalonOrderRole = (token: string | null | undefined): Promise<boolean> => {
  if (!token) return Promise.resolve(false);
  if (salonRoleCache?.token === token && Date.now() - salonRoleCache.at < SALON_ROLE_CACHE_MS) {
    return salonRoleCache.result;
  }
  const result = getUserRolesByToken()
    .then((res) => (res.data ?? []).some(
      (r) => r.roleId?.toLowerCase() === SALON_ORDER_ROLE_ID && r.userRoleIsEnabled !== false
    ))
    .catch(() => {
      salonRoleCache = null; // hata kalıcı olmasın, bir sonraki sayfada yeniden denensin
      return false;
    });
  salonRoleCache = { token, at: Date.now(), result };
  return result;
};

/** Backend'deki RoleConstants.RestaurantWaiterRole (salon personeli). */
export const RESTAURANT_WAITER_ROLE_ID = "d2a06612-23af-4c66-a3b9-1ba5ad6d6acd";

export interface IWaiterAccess {
  isSuperAdmin: boolean;
  isRestaurantAdmin: boolean;
  isWaiter: boolean;
  /**
   * 18 Eki 2026'dan beri salon uçları ve hub yalnız SalonSiparisUserRole + SuperAdmin kabul ediyor;
   * garson rolü tek başına salonu açmaz (her salon ucunda 403).
   */
  hasSalonRole: boolean;
}

/**
 * WaiterLogin sonrası rol kontrolü. Yalnızca garson rolü taşıyan hesap garson ekranına
 * kilitlenir; garson ekranından giren yönetici normal paneli kullanmaya devam eder.
 */
export const checkWaiterAccess = async (): Promise<IWaiterAccess> => {
  const res = await getUserRolesByToken();
  const roles: IUserRoles = (res.data ?? []).filter((r) => r.userRoleIsEnabled !== false);
  const has = (id: string) => roles.some((r) => r.roleId?.toLowerCase() === id);
  return {
    isSuperAdmin: has(SUPER_ADMIN_ROLE_ID),
    isRestaurantAdmin: has(RESTAURANT_ADMIN_ROLE_ID),
    isWaiter: has(RESTAURANT_WAITER_ROLE_ID),
    hasSalonRole: has(SALON_ORDER_ROLE_ID),
  };
};

export interface IDriverPanelAccess {
  isSuperAdmin: boolean;
  isRestaurantAdmin: boolean;
  isDriver: boolean;
}

/**
 * Ticket mesajına arka plan rengi ekleme yetkisi — backend'de yalnızca
 * RestourantAdmin / SuperAdmin rollerine izin veriyor, müşteri hesabı renk
 * gönderirse 403 MessageColorNotAllowed dönüyor. Renk seçici bu yüzden
 * sadece bu roller için gösterilmeli.
 */
export const checkCanSetMessageColor = async (): Promise<boolean> => {
  const userRoleResponse = await getUserRolesByToken();
  const userRoles: IUserRoles = userRoleResponse.data;
  return userRoles.some((r) => r.roleId === SUPER_ADMIN_ROLE_ID || r.roleId === RESTAURANT_ADMIN_ROLE_ID);
};

/**
 * DriverLogin ile giriş yapan kullanıcının rollerini, o an localStorage'daki
 * token üzerinden kontrol eder. RestaurantDriver, RestourantAdmin veya SuperAdmin
 * rollerinden biri kurye paneline erişim için yeterlidir; SuperAdmin/RestourantAdmin
 * bu ekrandan giriş yapsa bile diğer panel sayfalarına erişimi kısıtlanmaz — sadece
 * saf RestaurantDriver hesapları kurye paneline kilitlenir (bkz. Control.tsx).
 */
export const checkDriverPanelAccess = async (): Promise<IDriverPanelAccess> => {
  const userRoleResponse = await getUserRolesByToken();
  const userRoles: IUserRoles = userRoleResponse.data;
  return {
    isSuperAdmin: userRoles.some((r) => r.roleId === SUPER_ADMIN_ROLE_ID),
    isRestaurantAdmin: userRoles.some((r) => r.roleId === RESTAURANT_ADMIN_ROLE_ID),
    isDriver: userRoles.some((r) => r.roleId === RESTAURANT_DRIVER_ROLE_ID),
  };
};